/**
 * FloThumbnail — 공급 흐름도를 자르지 않고 전체를 줄여 보이는 미니 그림.
 * onClick 을 주면 그림 전체(노드·아래 안내 줄 포함)가 크게 보기 진입점이 된다 —
 *   키보드는 반응하지 않는다.
 * 크게 볼 수 있다는 안내는 hover 없이도 보이게 그림 아래 줄 오른쪽에 늘 둔다.
 * 레인 헤어라인·열 안내선은 FloSvg 가 그대로 그린다.
 * 고정 거터·눈금자(FloCanvas 의 오버레이)는 정적 이미지라 안 쓴다 — 레인 이름표도 없다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 *
 * props
 *   graph          좌표 없는 의미 그래프(floModel 헤더 참조).
 *                  렌더마다 새로 만들지 말고 useMemo 로 고정한다.
 *   maxHeight      그림 최대 높이(px). 기본 300.
 *   caption        그림 아래 줄 왼쪽에 놓는 호출자 문구(번역된 문자열). 없으면 비운다.
 *   onClick        주면 클릭으로 크게 보기를 연다 — 확대 안내도 이때만 보인다.
 *   foldLimit      잎 접기 기준(buildFloModel 과 같다).
 *   laneTypeOrder  레인 유형 정렬 순서(buildFloModel 과 같다).
 * 사용법·그래프 계약: saas-web/.claude/skills/flo/SKILL.md
 */
import { useId } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";

import { FS } from "@plannel/utils/font-util";
import useFlo from "@plannel/components/flo/useFlo";
import FloSvg from "@plannel/components/flo/FloSvg";

const FOOT_TEXT_SX = { fontSize: FS.CAPTION, color: "text.secondary", lineHeight: 1.5 };

export default function FloThumbnail({ graph, foldLimit, laneTypeOrder, maxHeight = 300, caption, onClick }) {
  const { t } = useTranslation();
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const { model, geometry, edges, fonts } = useFlo(graph, { foldLimit, laneTypeOrder });

  if (!model || !geometry) return null;

  return (
    <Box data-flo-thumbnail onClick={onClick} title={onClick ? t("floViewExpand") : undefined}
         sx={{
           width: "100%", minWidth: 0, borderRadius: "6px",
           border: "1px solid transparent", transition: "border-color 450ms ease",
           ...(onClick ? { cursor: "zoom-in", "&:hover": { borderColor: "divider" } } : null),
           "& > svg": { width: "100%", height: "auto", maxHeight, display: "block", borderRadius: "6px" },
         }}>
      <FloSvg model={model} geometry={geometry} edges={edges} fonts={fonts} mini
              viewBox={`0 0 ${geometry.width} ${geometry.height}`}
              width={geometry.width} height={geometry.height} markerBase={`flo-thumb-${uid}`} />
      {(caption || onClick) && (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, mt: "2px", px: "2px" }}>
          {caption && <Typography sx={FOOT_TEXT_SX}>{caption}</Typography>}
          {onClick && (
            <Box data-flo-expand-hint sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "3px" }}>
              <ZoomInOutlinedIcon sx={{ fontSize: 13, color: "text.secondary" }} />
              <Typography sx={FOOT_TEXT_SX}>{t("floViewExpandHint")}</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
